import { collection, doc, setDoc, getDoc, serverTimestamp, Timestamp, query, where, getDocs, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Channel } from '../types';
import { nanoid } from 'nanoid';
import logger from '../utils/logger';

interface SharedPlaylist {
  channels: Channel[];
  createdAt: Timestamp;
  userId: string;
}

const SHARE_COLLECTION = 'shared_playlists';

export const shareService = {
  async createSharedPlaylist(channels: Channel[], userId?: string): Promise<string> {
    try {
      if (!userId) {
        throw new Error('User ID is required to create a shared playlist');
      }

      // Delete any existing shares for this user first
      logger.debug('Deleting previous shares before creating new one');
      await this.deleteAllUserShares(userId);

      const shareId = nanoid(10); // Generate a short unique ID
      const now = Timestamp.now();

      const sharedPlaylist: SharedPlaylist = {
        channels,
        createdAt: now,
        userId: userId
      };

      logger.debug('Creating shared playlist:', {
        channelCount: channels.length
      });

      const shareRef = doc(db, SHARE_COLLECTION, shareId);
      await setDoc(shareRef, {
        ...sharedPlaylist,
        createdAt: serverTimestamp(),
      });

      return shareId;
    } catch (error) {
      console.error('Error creating shared playlist');
      throw new Error('Failed to create shared playlist');
    }
  },

  async getSharedPlaylist(shareId: string): Promise<Channel[] | null> {
    try {
      const shareRef = doc(db, SHARE_COLLECTION, shareId);
      const shareDoc = await getDoc(shareRef);

      if (!shareDoc.exists()) {
        return null;
      }

      const data = shareDoc.data() as SharedPlaylist;
      
      return data.channels;
    } catch (error) {
      console.error('Error fetching shared playlist:', error);
      throw new Error('Failed to fetch shared playlist');
    }
  },

  generateShareUrl(shareId: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/import/${shareId}`;
  },

  extractShareId(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      const shareId = pathParts[pathParts.indexOf('import') + 1];
      return shareId || null;
    } catch {
      return null;
    }
  },

  async deleteAllUserShares(userId: string): Promise<void> {
    try {
      logger.debug('Deleting previous shares');
      const sharesRef = collection(db, SHARE_COLLECTION);
      const userSharesQuery = query(sharesRef, where('userId', '==', userId));
      const snapshot = await getDocs(userSharesQuery);

      logger.debug('Found shares to delete:', snapshot.size);

      // If no shares, return immediately
      if (snapshot.empty) return;

      // Split into batches of 500 (Firestore batch limit)
      const batchSize = 500;
      const batches: Promise<void>[] = [];

      for (let i = 0; i < snapshot.docs.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchDocs = snapshot.docs.slice(i, i + batchSize);
        
        batchDocs.forEach(doc => {
          batch.delete(doc.ref);
        });

        // Don't await here, just collect the promises
        batches.push(batch.commit());
      }

      // Execute all batches in parallel
      await Promise.all(batches);
      logger.debug('Successfully deleted shares');
    } catch (error) {
      console.error('Error deleting shares');
    }
  }
}; 